import { supabase } from '@/integrations/supabase/client';
import { CURRENT_PROFILE_KEY } from '@/hooks/useCurrentProfile';
import type { QueryClient } from '@tanstack/react-query';

/**
 * Check if the user needs welcome onboarding — compares acceptance + version.
 * Returns true if never accepted OR if the configured post_login version is newer
 * than the user's last accepted version.
 */
export function checkWelcomeFromCache(
  welcomeAcceptedAt: string | null | undefined,
  userVersion: number | null | undefined = 0,
  configVersion: number | null | undefined = 1,
): boolean {
  if (welcomeAcceptedAt === null || welcomeAcceptedAt === undefined) return true;
  return (userVersion ?? 0) < (configVersion ?? 1);
}

/**
 * Mark the welcome onboarding as accepted at the given version and invalidate profile cache
 */
export async function acceptWelcomeOnboarding(
  userId: string,
  queryClient?: QueryClient,
  version: number = 1,
): Promise<boolean> {
  const { data, error } = await supabase.rpc('accept_my_welcome_onboarding', {
    _version: version,
  });

  if (error) {
    console.error('Error accepting welcome onboarding:', error);
    return false;
  }

  const result = Array.isArray(data) ? data[0] : null;
  if (!result?.welcome_onboarding_accepted_at) {
    console.error('Error accepting welcome onboarding: no updated profile returned');
    return false;
  }

  // Patch the exact cache entry before the modal advances. This prevents a
  // stale background revalidation from reopening the onboarding in the same
  // session, while the invalidation keeps every other profile consumer synced.
  queryClient?.setQueryData(
    [CURRENT_PROFILE_KEY, userId],
    (previous: Record<string, unknown> | null | undefined) => previous
      ? {
          ...previous,
          welcome_onboarding_accepted_at: result.welcome_onboarding_accepted_at,
          welcome_onboarding_version: result.welcome_onboarding_version,
        }
      : previous,
  );
  queryClient?.invalidateQueries({ queryKey: [CURRENT_PROFILE_KEY] });
  return true;
}
